import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react"

import { useIsMounted } from "hooks/useIsMounted"

import { ReloadStatus, UserReload } from "services/user/type"
import { userService } from "services/user/user.service"

import { ActiveReloadCard } from "./ActiveReloadCard"
import { TabHandle } from "./CustomBonusTab"
import { ReloadForm } from "./ReloadForm"

interface ReloadTabProps {
  userId: string
  open: boolean
  onSuccess: () => void
  onClose: () => void
  onActiveChange?: (hasActive: boolean) => void
}

export const ReloadTab = forwardRef<TabHandle, ReloadTabProps>(({ userId, open, onSuccess, onClose, onActiveChange }, ref) => {
  const isMounted = useIsMounted()

  const formRef = useRef<TabHandle>(null)
  const [activeReload, setActiveReload] = useState<UserReload | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const loadActiveReload = useCallback(async () => {
    setIsLoading(true)
    const reloads = await userService.getUserReloads(userId)
    if (!isMounted()) return
    const active = reloads.find((r) => r.status === ReloadStatus.ACTIVE) ?? null
    setActiveReload(active)
    setIsLoading(false)
  }, [userId, isMounted])

  useEffect(() => {
    if (open) loadActiveReload()
  }, [open, loadActiveReload])

  useEffect(() => {
    onActiveChange?.(!!activeReload)
  }, [activeReload, onActiveChange])

  useImperativeHandle(ref, () => ({
    handleSubmit: () => {
      if (activeReload) return
      formRef.current?.handleSubmit()
    },
    isSubmitting: formRef.current?.isSubmitting ?? false
  }))

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-10">
        <span className="loading loading-spinner loading-md" />
      </div>
    )
  }

  if (activeReload) {
    return (
      <ActiveReloadCard
        reload={activeReload}
        onCancelled={() => {
          setActiveReload(null)
          onSuccess()
        }}
      />
    )
  }

  return <ReloadForm ref={formRef} userId={userId} onSuccess={onSuccess} onClose={onClose} />
})

ReloadTab.displayName = "ReloadTab"
